import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import moment from "moment";
import { FiArrowRight } from "react-icons/fi";
import { EmptyState, PageHeader } from "../../components/dashboard/DashboardUI";
import RequestDetail from "../../components/modals/RequestDetail";
import {
  getAdminAllServicesTickets,
  getTicketTypes,
} from "../../redux/actions/TicketActions";
import { fetchAdminProcedures } from "../../redux/actions/ProcedureActions";
import { getTransactions } from "../../redux/actions/TransactionActions";
import { requestTitle } from "../../utils/requestDisplay";
import { SERVICE_PAGES } from "../../utils/operatorPages";
import { formatPrice } from "../../utils/formatPrice";

const MAX_WAITING = 6;

// Admin pages of the services (no admin page for ERP accounts)
const ADMIN_SERVICES = SERVICE_PAGES.map((service) => ({
  ...service,
  path:
    service.code === "odoo"
      ? null
      : service.path.replace("/operator/", "/admin/"),
}));

function AdminHome() {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.authUser);
  const { ticketTypes, adminAllServicesTickets } = useSelector(
    (state) => state.tickets,
  );
  const { procedures } = useSelector((state) => state.procedures);
  const { transactions } = useSelector((state) => state.transactions);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!user?.token) return;

    setLoading(true);
    Promise.all([
      dispatch(getTicketTypes()),
      dispatch(getAdminAllServicesTickets(user.token)),
      dispatch(fetchAdminProcedures(user.token)),
      dispatch(getTransactions(user.token)),
    ]).finally(() => setLoading(false));
  }, [dispatch, user?.token]);

  const requests = procedures || [];
  const waiting = requests
    .filter((r) => r.status === "paid")
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  const inProgress = requests.filter((r) => r.status === "in_progress");
  const tickets = adminAllServicesTickets || [];
  const pendingTickets = tickets.filter((t) => t.status === "initiated");

  const monthRevenue = (transactions || [])
    .filter(
      (t) =>
        t.status === "completed" && moment(t.createdAt).isSame(moment(), "month"),
    )
    .reduce((total, t) => total + (Number(t.amount) || 0), 0);

  // Tickets of each service, the service being known from the ticket type
  const serviceOf = (ticket) =>
    (ticketTypes || [])
      .find((item) => item.code === ticket.reclamationCode)
      ?.service?.toLowerCase() || "odoo";
  const ticketStats = ADMIN_SERVICES.map((service) => {
    const own = tickets.filter((ticket) => serviceOf(ticket) === service.code);
    const count = (status) => own.filter((t) => t.status === status).length;

    return {
      ...service,
      pending: count("initiated"),
      inProgress: count("in_progress"),
    };
  });

  const stats = [
    { label: "Démarches non prises en charge", value: waiting.length },
    { label: "Démarches en cours", value: inProgress.length },
    { label: "Demandes de services en attente", value: pendingTickets.length },
    {
      label: "Encaissé ce mois-ci",
      value: formatPrice(monthRevenue),
    },
  ];

  return (
    <>
      <PageHeader
        title="Tableau de bord"
        lead={`Bonjour ${user?.firstname || ""}, voici l'activité de la plateforme.`}
      />

      <div className="dash-stats is-four">
        {stats.map((stat) => (
          <div key={stat.label} className="dash-card dash-stat">
            <span>{stat.label}</span>
            <strong>{loading ? "…" : stat.value}</strong>
          </div>
        ))}
      </div>

      <div className="dash-home-grid">
        <section className="dash-card">
          <div className="dash-card-head">
            <h2 className="dash-card-title">En attente de prise en charge</h2>
            <Link to="/admin/procedures" className="dash-card-link">
              Toutes les démarches <FiArrowRight aria-hidden="true" />
            </Link>
          </div>

          {loading ? (
            <p className="dash-loading">Chargement…</p>
          ) : waiting.length === 0 ? (
            <EmptyState title="Rien en attente">
              Toutes les démarches payées sont prises en charge.
            </EmptyState>
          ) : (
            <ul className="dash-list">
              {waiting.slice(0, MAX_WAITING).map((request) => (
                <li key={request.reference}>
                  <div>
                    <span className="dash-cell-title">
                      {requestTitle(request)}
                    </span>
                    <span className="dash-cell-sub">
                      #{request.reference} · payée le{" "}
                      {moment(request.updatedAt || request.createdAt).format(
                        "DD/MM/YYYY",
                      )}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="dash-link-button"
                    onClick={() => setSelected(request)}
                  >
                    Voir
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="dash-card">
          <h2 className="dash-card-title">Demandes aux services</h2>
          <div className="dash-table-wrap">
            <table className="dash-table">
              <thead>
                <tr>
                  <th>Service</th>
                  <th>En attente</th>
                  <th>En cours</th>
                </tr>
              </thead>
              <tbody>
                {ticketStats.map((service) => (
                  <tr key={service.code}>
                    <td>
                      {service.path ? (
                        <Link to={service.path} className="dash-table-link">
                          {service.label}
                        </Link>
                      ) : (
                        service.label
                      )}
                    </td>
                    <td>
                      {service.pending > 0 ? (
                        <strong>{service.pending}</strong>
                      ) : (
                        "0"
                      )}
                    </td>
                    <td>{service.inProgress}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <RequestDetail request={selected} onHide={() => setSelected(null)} />
    </>
  );
}

export default AdminHome;
